import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  getCommandById,
  getCommandLookup,
  sendCommandByVehicleId,
  updateCommand,
  type SendCommandPayload,
  type UpdateCommandPayload,
} from "src/services/commandService";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import { useGeneralRefetchOverride } from "./general.queries";
import { QueryNames } from "./queries.constant";

type QueryOptions = { enabled?: boolean } & Record<string, unknown>;

export const useQueryGetCommandLookup = (
  modelId?: string | number,
  options: QueryOptions = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = !!modelId && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.commandLookup, modelId, language],
      queryFn: () => getCommandLookup(modelId as string | number, language),
      enabled,
      select: (data) => data.data.response,
      retry: false,
      ...options,
    }),
    enabled,
  );
};

export const useQueryGetCommandById = (
  commandId?: string | number,
  options: QueryOptions = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = !!commandId && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.commandDetail, commandId, language],
      queryFn: () => getCommandById(commandId as string | number, language),
      enabled,
      select: (data) => data.data.response,
      retry: false,
      ...options,
    }),
    enabled,
  );
};

export const useMutationUpdateCommand = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      commandId,
      payload,
    }: {
      commandId: string | number;
      payload: UpdateCommandPayload;
    }) => updateCommand(commandId, payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QueryNames.commandDetail,
      });
    },
  });
};

export const useMutationSendCommand = () => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      vehicleId,
      payload,
    }: {
      vehicleId: string | number;
      payload: SendCommandPayload;
    }) => sendCommandByVehicleId(vehicleId, payload, language),
  });
};
